import React, { useEffect, useState } from 'react';
import { categoryApi, ruleApi, mailAccountApi, authApi, myCustomerApi, mailApi, attachmentApi, outboundAttachmentApi, contactApi } from '../api/client';
import useAuthStore from '../hooks/useAuth';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  isPushSupported, getNotificationPermission, getCurrentSubscription,
  enablePushNotifications, disablePushNotifications,
} from '../push';

const TABS = ['Kategorien', 'Regeln', 'Mailkonten', 'Aufbewahrung', 'Spam-Filter', 'HTML-Mails', 'Benachrichtigungen', 'Kontaktliste'];

const FIELD_LABELS = {
  sender: 'Absender', subject: 'Betreff', recipient: 'Empfänger',
  has_attachment: 'Anhang vorhanden', keywords: 'Schlüsselwörter', negative_keywords: 'Negativwörter',
};
const OP_LABELS = {
  contains: 'enthält', not_contains: 'enthält nicht',
  equals: 'ist gleich', starts_with: 'beginnt mit', is_true: 'ist wahr',
};

export default function PortalSettings() {
  const { user } = useAuthStore();
  const [tab, setTab] = useState(0);

  // Daten
  const [categories, setCategories] = useState([]);
  const [rules, setRules] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [customerSettings, setCustomerSettings] = useState(null);
  const [meSettings, setMeSettings] = useState(null);
  const [largeAttachments, setLargeAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(true);
  const [attachmentActionId, setAttachmentActionId] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [contactActionId, setContactActionId] = useState(null);
  const [editingContactId, setEditingContactId] = useState(null);
  const [editContactName, setEditContactName] = useState('');

  const [loading, setLoading] = useState(true);
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [ruleForm, setRuleForm] = useState({
    name: '', field: 'sender', operator: 'contains', value: '', category: '', priority: 0, is_active: true,
  });
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [showCatForm, setShowCatForm] = useState(false);
  const [catForm, setCatForm] = useState({ name: '', target_folder: '', auto_move_enabled: false, min_confidence: 0.7, show_in_portal: true, show_in_mobile: true, push_enabled: false });

  // Push-Benachrichtigungen (Browser/PWA)
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [pushError, setPushError] = useState('');

  useEffect(() => {
    Promise.all([
      categoryApi.list(),
      ruleApi.list(),
      mailAccountApi.list(),
      myCustomerApi.get(),
      authApi.me(),
    ]).then(([c, r, a, cs, me]) => {
      setCategories(c.data.results || c.data);
      setRules(r.data.results || r.data);
      setAccounts(a.data.results || a.data);
      setCustomerSettings(cs.data);
      setMeSettings(me.data);
    }).finally(() => setLoading(false));
    loadLargeAttachments();
    loadContacts();
  }, []);

  const loadLargeAttachments = () => {
    setAttachmentsLoading(true);
    myCustomerApi.largeAttachments()
      .then(({ data }) => setLargeAttachments(data))
      .catch(() => setLargeAttachments([]))
      .finally(() => setAttachmentsLoading(false));
  };

  const loadContacts = () => {
    setContactsLoading(true);
    contactApi.list()
      .then(({ data }) => setContacts(data.results || data))
      .catch(() => setContacts([]))
      .finally(() => setContactsLoading(false));
  };

  const handleDeleteContact = async (contact) => {
    if (!window.confirm(`Kontakt "${contact.display_name || contact.email}" wirklich löschen?`)) return;
    setContactActionId(contact.id);
    try {
      await contactApi.delete(contact.id);
      setContacts(prev => prev.filter(c => c.id !== contact.id));
      showToast('Kontakt gelöscht.');
    } catch {
      showToast('Löschen fehlgeschlagen.');
    } finally {
      setContactActionId(null);
    }
  };

  const startEditContact = (contact) => {
    setEditingContactId(contact.id);
    setEditContactName(contact.display_name || '');
  };

  const cancelEditContact = () => {
    setEditingContactId(null);
    setEditContactName('');
  };

  const handleSaveContactName = async (contact) => {
    setContactActionId(contact.id);
    try {
      const { data } = await contactApi.update(contact.id, { display_name: editContactName.trim() });
      setContacts(prev => prev.map(c => c.id === contact.id ? data : c));
      cancelEditContact();
      showToast('Kontakt gespeichert.');
    } catch {
      showToast('Speichern fehlgeschlagen.');
    } finally {
      setContactActionId(null);
    }
  };

  const refreshStorageUsage = () => {
    myCustomerApi.get().then(({ data }) => setCustomerSettings(data)).catch(() => {});
  };

  const formatSize = (bytes) => bytes > 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.round(bytes / 1024)} KB`;

  const handleDownloadAttachment = async (att) => {
    try {
      const api = att.source === 'sent' ? outboundAttachmentApi : attachmentApi;
      const { data } = await api.download(att.id);
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showToast('Download fehlgeschlagen.');
    }
  };

  const handleDeleteAttachment = async (att) => {
    if (!window.confirm(`Anhang "${att.filename}" wirklich löschen? Die zugehörige Mail bleibt erhalten, nur der Anhang wird entfernt.`)) return;
    setAttachmentActionId(att.id);
    try {
      const api = att.source === 'sent' ? outboundAttachmentApi : attachmentApi;
      await api.delete(att.id);
      setLargeAttachments(prev => prev.filter(a => a.id !== att.id));
      refreshStorageUsage();
      showToast('Anhang gelöscht.');
    } catch {
      showToast('Löschen fehlgeschlagen.');
    } finally {
      setAttachmentActionId(null);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  };

  // ── Push-Benachrichtigungen ─────────────────────────────────────────────────
  const refreshPushStatus = async () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    setPushPermission(getNotificationPermission());
    if (supported) {
      const sub = await getCurrentSubscription().catch(() => null);
      setPushSubscribed(!!sub);
    }
  };

  useEffect(() => { refreshPushStatus(); }, []);

  const handleEnablePush = async () => {
    setPushBusy(true);
    setPushError('');
    try {
      await enablePushNotifications();
      await refreshPushStatus();
      showToast('Push-Benachrichtigungen aktiviert.');
    } catch (e) {
      setPushError(e.message || 'Aktivierung fehlgeschlagen.');
    } finally {
      setPushBusy(false);
    }
  };

  const handleDisablePush = async () => {
    setPushBusy(true);
    setPushError('');
    try {
      await disablePushNotifications();
      await refreshPushStatus();
      showToast('Push-Benachrichtigungen deaktiviert.');
    } catch (e) {
      setPushError(e.message || 'Deaktivierung fehlgeschlagen.');
    } finally {
      setPushBusy(false);
    }
  };

  // ── Kategorien ──────────────────────────────────────────────────────────────
  const handleUpdateCategory = async (id, updates) => {
    await categoryApi.update(id, updates);
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...updates } : c));
  };

  const handleResetAllClassifications = async () => {
    if (!window.confirm('Wirklich alle Kategorisierungen zurücksetzen? Alle Mails verlieren ihre Kategorie und müssen neu klassifiziert werden.')) return;
    setSaving(true);
    try {
      const { data } = await mailApi.resetAllClassifications();
      showToast(data.detail);
    } catch {
      showToast('Fehler beim Zurücksetzen.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const slug = catForm.name.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      await categoryApi.create({ ...catForm, slug });
      const { data } = await categoryApi.list();
      setCategories(data.results || data);
      setShowCatForm(false);
      setCatForm({ name: '', target_folder: '', auto_move_enabled: false, min_confidence: 0.7, show_in_portal: true, show_in_mobile: true, push_enabled: false });
      showToast('Kategorie angelegt.');
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Kategorie wirklich löschen? Alle zugeordneten Mails verlieren diese Kategorie.')) return;
    await categoryApi.delete(id);
    setCategories(prev => prev.filter(c => c.id !== id));
    showToast('Kategorie gelöscht.');
  };

  // ── Regeln ──────────────────────────────────────────────────────────────────
  const handleSaveRule = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await ruleApi.create(ruleForm);
      const { data } = await ruleApi.list();
      setRules(data.results || data);
      setShowRuleForm(false);
      setRuleForm({ name: '', field: 'sender', operator: 'contains', value: '', category: '', priority: 0, is_active: true });
      showToast('Regel gespeichert.');
    } catch (err) {
      alert(JSON.stringify(err.response?.data));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (id) => {
    if (!window.confirm('Regel wirklich löschen?')) return;
    await ruleApi.delete(id);
    setRules(prev => prev.filter(r => r.id !== id));
    showToast('Regel gelöscht.');
  };

  const handleApplyAllRules = async () => {
    if (!window.confirm('Alle Regeln auf vorhandene Mails anwenden? Die Kategorisierung bereits verarbeiteter Mails kann sich dadurch ändern.')) return;
    setSaving(true);
    try {
      const { data } = await ruleApi.applyAll();
      showToast(data.detail);
    } catch {
      showToast('Fehler beim Anwenden der Regeln.');
    } finally {
      setSaving(false);
    }
  };

  // ── Aufbewahrung ────────────────────────────────────────────────────────────
  const isAdmin = user?.role === 'customer_admin' || user?.role === 'superadmin';

  const handleSaveRetention = async () => {
    setSaving(true);
    try {
      await myCustomerApi.update({
        retention_unlimited: customerSettings.retention_unlimited,
        retention_days: customerSettings.retention_days,
      });
      showToast('Aufbewahrungseinstellung gespeichert.');
    } catch {
      showToast('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  // ── Spam-Filter ─────────────────────────────────────────────────────────────
  const handleSaveSpam = async () => {
    setSaving(true);
    try {
      await myCustomerApi.update({ spam_action: customerSettings.spam_action });
      showToast('Spam-Filter gespeichert.');
    } catch {
      showToast('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  // ── HTML-Mail-Anzeige ───────────────────────────────────────────────────────
  const handleSaveHtmlMailDisplay = async () => {
    setSaving(true);
    try {
      await myCustomerApi.update({ html_mail_display: customerSettings.html_mail_display });
      showToast('Einstellung gespeichert.');
    } catch {
      showToast('Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  // ── Benachrichtigungen ──────────────────────────────────────────────────────
  const handleToggleNotify = async (value) => {
    try {
      await authApi.updateMe({ notify_on_new_mail: value });
      setMeSettings(prev => ({ ...prev, notify_on_new_mail: value }));
      showToast(value ? 'Benachrichtigungen aktiviert.' : 'Benachrichtigungen deaktiviert.');
    } catch {
      showToast('Fehler beim Speichern.');
    }
  };

  if (loading) return <LoadingSpinner size="lg" />;

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Einstellungen</h1>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg z-50 transition-opacity">
          {toast}
        </div>
      )}

      {/* Tab-Navigation */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 overflow-x-auto">
        {TABS.map((t, i) => (
          <button key={t} onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
              tab === i ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}>
            {t}
          </button>
        ))}
      </div>

      {/* ── Tab 0: Kategorien ──────────────────────────────────────────────── */}
      {tab === 0 && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              Die KI ordnet eingehende Mails automatisch diesen Kategorien zu.
            </p>
            <button onClick={() => setShowCatForm(!showCatForm)}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 whitespace-nowrap">
              + Neue Kategorie
            </button>
          </div>

          {showCatForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
              <h3 className="font-semibold mb-4 text-gray-800">Neue Kategorie anlegen</h3>
              <form onSubmit={handleCreateCategory} className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name *</label>
                  <input required value={catForm.name}
                    onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="z.B. Rechnung"
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className={`block text-xs font-medium mb-1 ${catForm.auto_move_enabled ? 'text-gray-600' : 'text-gray-400'}`}>
                    Zielordner (IMAP){catForm.auto_move_enabled ? ' *' : ''}
                  </label>
                  <input
                    required={catForm.auto_move_enabled}
                    disabled={!catForm.auto_move_enabled}
                    value={catForm.target_folder}
                    onChange={e => setCatForm(f => ({ ...f, target_folder: e.target.value }))}
                    placeholder="z.B. INBOX.Rechnungen"
                    className={`w-full px-2.5 py-1.5 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${
                      catForm.auto_move_enabled
                        ? 'border-gray-300 bg-white'
                        : 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                    }`} />
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <input type="checkbox" id="cat_auto_move" checked={catForm.auto_move_enabled}
                    onChange={e => setCatForm(f => ({ ...f, auto_move_enabled: e.target.checked, target_folder: e.target.checked ? f.target_folder : '' }))}
                    className="rounded border-gray-300 text-blue-600" />
                  <label htmlFor="cat_auto_move" className="text-xs text-gray-600">
                    Automatisch verschieben
                    {catForm.auto_move_enabled && <span className="ml-1 text-amber-600">(⚠ Live-Mails auf dem Mailserver werden verschoben!)</span>}
                  </label>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Min. Confidence (0–1)</label>
                  <input type="number" min="0" max="1" step="0.05" value={catForm.min_confidence}
                    onChange={e => setCatForm(f => ({ ...f, min_confidence: parseFloat(e.target.value) }))}
                    className="w-24 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div className="col-span-2 flex flex-wrap gap-4 pt-1">
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={catForm.show_in_portal}
                      onChange={e => setCatForm(f => ({ ...f, show_in_portal: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600" />
                    Im Portal anzeigen
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={catForm.show_in_mobile}
                      onChange={e => setCatForm(f => ({ ...f, show_in_mobile: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600" />
                    In Mobile-App anzeigen
                  </label>
                  <label className="flex items-center gap-2 text-xs text-gray-600">
                    <input type="checkbox" checked={catForm.push_enabled}
                      onChange={e => setCatForm(f => ({ ...f, push_enabled: e.target.checked }))}
                      className="rounded border-gray-300 text-blue-600" />
                    Push-Benachrichtigung
                  </label>
                </div>
                <div className="col-span-2 flex gap-2 pt-1">
                  <button type="submit" disabled={saving}
                    className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                    {saving ? 'Speichern...' : 'Anlegen'}
                  </button>
                  <button type="button" onClick={() => setShowCatForm(false)}
                    className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50">
                    Abbrechen
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  {['Kategorie', 'Zielordner (IMAP)', 'Auto-Verschieben auf Mailserver', 'Min. Confidence', 'Portal', 'Mobile', 'Push', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {categories.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-400 text-sm">
                      Noch keine Kategorien. Lege deine erste Kategorie an.
                    </td>
                  </tr>
                )}
                {categories.map(c => (
                  <tr key={c.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">
                      {c.name}
                      {c.is_system && <span className="ml-2 text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">System</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-mono">{c.target_folder}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <input type="checkbox" checked={c.auto_move_enabled}
                          disabled={c.is_system}
                          onChange={e => handleUpdateCategory(c.id, { auto_move_enabled: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                        {c.auto_move_enabled && (
                          <span className="text-xs text-amber-600 font-medium">⚠ Live</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <input type="number" min="0" max="1" step="0.05" value={c.min_confidence}
                        onChange={e => handleUpdateCategory(c.id, { min_confidence: parseFloat(e.target.value) })}
                        className="w-16 px-2 py-1 border border-gray-300 rounded text-xs focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                    </td>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={c.show_in_portal}
                        onChange={e => handleUpdateCategory(c.id, { show_in_portal: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600" />
                    </td>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={c.show_in_mobile}
                        onChange={e => handleUpdateCategory(c.id, { show_in_mobile: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600" />
                    </td>
                    <td className="px-4 py-3">
                      <input type="checkbox" checked={c.push_enabled}
                        onChange={e => handleUpdateCategory(c.id, { push_enabled: e.target.checked })}
                        className="rounded border-gray-300 text-blue-600" />
                    </td>
                    <td className="px-4 py-3">
                      {!c.is_system && (
                        <button onClick={() => handleDeleteCategory(c.id)}
                          className="text-xs text-red-500 hover:text-red-700">
                          Löschen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 border border-red-200 rounded-xl p-4 bg-red-50">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-red-800">Testwerkzeug: Alle Klassifizierungen zurücksetzen</div>
                <div className="text-xs text-red-600 mt-0.5">
                  Entfernt Kategorie, Confidence und Klassifizierungsgrund von allen Mails. Nur für Testzwecke.
                </div>
              </div>
              <button onClick={handleResetAllClassifications} disabled={saving}
                className="ml-4 px-4 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 whitespace-nowrap">
                Klassifizierungen zurücksetzen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Tab 1: Regeln ──────────────────────────────────────────────────── */}
      {tab === 1 && (
        <div>
          <div className="flex justify-end gap-2 mb-4">
            <button onClick={handleApplyAllRules} disabled={saving || rules.length === 0}
              className="px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 disabled:opacity-50">
              ▶ Regeln auf alle Mails anwenden
            </button>
            <button onClick={() => setShowRuleForm(!showRuleForm)}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
              + Neue Regel
            </button>
          </div>

          {showRuleForm && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-4">
              <h3 className="font-semibold mb-4">Neue Klassifizierungsregel</h3>
              <form onSubmit={handleSaveRule} className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                  <input required value={ruleForm.name}
                    onChange={e => setRuleForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Feld</label>
                  <select value={ruleForm.field}
                    onChange={e => setRuleForm(f => ({ ...f, field: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    {Object.entries(FIELD_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Operator</label>
                  <select value={ruleForm.operator}
                    onChange={e => setRuleForm(f => ({ ...f, operator: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    {Object.entries(OP_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Wert <span className="text-gray-400 font-normal">(mehrere per Komma: Rechnung, Faktura, Invoice)</span></label>
                  <input value={ruleForm.value}
                    onChange={e => setRuleForm(f => ({ ...f, value: e.target.value }))}
                    placeholder="z.B. Rechnung, Faktura, Invoice"
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Zielkategorie</label>
                  <select required value={ruleForm.category}
                    onChange={e => setRuleForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none">
                    <option value="">Auswählen...</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Priorität</label>
                  <input type="number" value={ruleForm.priority}
                    onChange={e => setRuleForm(f => ({ ...f, priority: parseInt(e.target.value) }))}
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div className="col-span-2 flex gap-2 justify-end">
                  <button type="button" onClick={() => setShowRuleForm(false)}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm">Abbrechen</button>
                  <button type="submit" disabled={saving}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">
                    {saving ? 'Speichern...' : 'Anlegen'}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500 uppercase text-xs">
                <tr>
                  {['Name', 'Feld', 'Operator', 'Wert', 'Kategorie', 'Priorität', 'Aktiv', ''].map(h => (
                    <th key={h} className="px-4 py-3 text-left font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rules.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium">{r.name}</td>
                    <td className="px-4 py-3 text-gray-500">{FIELD_LABELS[r.field] || r.field}</td>
                    <td className="px-4 py-3 text-gray-500">{OP_LABELS[r.operator] || r.operator}</td>
                    <td className="px-4 py-3 font-mono text-xs">{r.value || '–'}</td>
                    <td className="px-4 py-3">
                      <span className="bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-full">{r.category_name}</span>
                    </td>
                    <td className="px-4 py-3 text-center">{r.priority}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${r.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                        {r.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleDeleteRule(r.id)}
                        className="text-red-500 hover:text-red-700 text-xs">Löschen</button>
                    </td>
                  </tr>
                ))}
                {rules.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Keine Regeln definiert.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Tab 2: Mailkonten ──────────────────────────────────────────────── */}
      {tab === 2 && (
        <div className="space-y-3">
          {accounts.map(a => (
            <div key={a.id} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{a.email_address}</div>
                  <div className="text-xs text-gray-500">
                    {a.display_name || 'Kein Anzeigename'} · {a.imap_host}:{a.imap_port}
                  </div>
                  {a.last_checked_at && (
                    <div className="text-xs text-gray-400">
                      Zuletzt: {new Date(a.last_checked_at).toLocaleString('de-DE')}
                    </div>
                  )}
                  {a.last_error && <div className="text-xs text-red-600 mt-1">{a.last_error}</div>}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  a.status === 'active' ? 'bg-green-100 text-green-700' :
                  a.status === 'error' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {a.status === 'active' ? 'Aktiv' : a.status === 'error' ? 'Fehler' : 'Pausiert'}
                </span>
              </div>
            </div>
          ))}
          {accounts.length === 0 && (
            <div className="text-center text-gray-400 py-10 text-sm">Keine Mailkonten konfiguriert.</div>
          )}
        </div>
      )}

      {/* ── Tab 3: Aufbewahrung ────────────────────────────────────────────── */}
      {tab === 3 && customerSettings && (
        <div className="max-w-3xl space-y-6">
        <div className="max-w-lg space-y-6">
          {customerSettings.storage_limit_mb > 0 && (() => {
            const used = customerSettings.storage_used_mb ?? 0;
            const limit = customerSettings.storage_limit_mb;
            const pct = Math.min(used / limit, 1);
            const barColor = pct >= 1 ? 'bg-red-500' : pct >= 0.9 ? 'bg-amber-500' : 'bg-blue-500';
            const textColor = pct >= 1 ? 'text-red-600' : pct >= 0.9 ? 'text-amber-600' : 'text-gray-700';
            return (
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h2 className="font-semibold text-gray-800 mb-1">Speichernutzung</h2>
                <p className="text-sm text-gray-500 mb-4">
                  Belegter Speicher durch empfangene und versendete Anhänge.
                </p>
                <div className="flex justify-between items-baseline mb-1.5">
                  <span className="text-sm text-gray-500">Aktuell verbraucht</span>
                  <span className={`text-sm font-medium ${textColor}`}>
                    {used.toLocaleString('de-DE')} MB / {limit.toLocaleString('de-DE')} MB
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2.5">
                  <div className={`h-2.5 rounded-full ${barColor}`} style={{ width: `${Math.round(pct * 100)}%` }} />
                </div>
                {pct >= 0.9 ? (
                  <div className={`text-xs mt-2 ${textColor}`}>
                    {Math.round(pct * 100)}% belegt{pct >= 1 ? ' — Limit überschritten. Bitte nicht mehr benötigte Anhänge löschen oder Aufbewahrungsfrist verkürzen.' : ' — Limit fast erreicht.'}
                  </div>
                ) : (
                  <div className="text-xs text-gray-400 mt-2">{Math.round(pct * 100)}% belegt</div>
                )}
                <p className="text-xs text-gray-400 mt-3">
                  Das maximal verfügbare Speicherlimit legt dein Administrator fest.
                </p>
              </div>
            );
          })()}

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-gray-800 mb-1">Aufbewahrungsfrist für Mailinhalte</h2>
              <p className="text-sm text-gray-500">
                Volltext und Anhänge werden nach Ablauf dieser Frist automatisch gelöscht.
                Metadaten (Absender, Betreff, Datum) bleiben dauerhaft erhalten.
              </p>
            </div>

            <div>
              <label className={`flex items-start gap-2.5 ${isAdmin ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                <input
                  type="checkbox"
                  checked={customerSettings.retention_unlimited}
                  disabled={!isAdmin}
                  onChange={e => setCustomerSettings(s => ({ ...s, retention_unlimited: e.target.checked }))}
                  className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                />
                <span>
                  <span className="text-sm font-medium text-gray-800">Unbefristet aufbewahren</span>
                  <span className="block text-xs text-gray-500">
                    Kein automatisches Löschen von Inhalten/Anhängen (technischer Standard).
                  </span>
                </span>
              </label>
            </div>

            <div className={customerSettings.retention_unlimited ? 'opacity-50' : ''}>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Aufbewahrungsfrist (Tage)
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="number" min="1" max="3650"
                  value={customerSettings.retention_days}
                  disabled={!isAdmin || customerSettings.retention_unlimited}
                  onChange={e => setCustomerSettings(s => ({ ...s, retention_days: parseInt(e.target.value) || 90 }))}
                  className="w-28 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none disabled:bg-gray-50 disabled:text-gray-400"
                />
                <span className="text-sm text-gray-500">Tage</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                Nur wirksam, wenn "Unbefristet aufbewahren" oben deaktiviert ist. Volltext und Anhänge
                werden dann nach Ablauf dieser Frist automatisch gelöscht — Metadaten
                (Absender, Betreff, Datum) bleiben in jedem Fall dauerhaft erhalten.
              </p>
              {!isAdmin && (
                <p className="text-xs text-gray-400 mt-1">
                  Nur Kunden-Admins können diese Einstellung ändern.
                </p>
              )}
            </div>

            {!customerSettings.retention_unlimited && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                <strong>Hinweis:</strong> Kürzere Fristen löschen Inhalte früher und schonen Speicher.
                Längere Fristen verbessern die Nachvollziehbarkeit. Gesetzliche Aufbewahrungspflichten
                (z.B. GoBD: 10 Jahre für Geschäftspost) bitte selbst beachten.
              </div>
            )}

            {isAdmin && (
              <button
                onClick={handleSaveRetention}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            )}
          </div>
        </div>

          {/* Größte Anhänge */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-800 mb-1">Größte Anhänge</h2>
            <p className="text-sm text-gray-500 mb-4">
              Empfangene und selbst gesendete Anhänge, absteigend nach Größe. Herunterladen sichert
              den Inhalt lokal, Löschen entfernt nur den Anhang — die zugehörige Mail bleibt erhalten.
            </p>
            {attachmentsLoading ? (
              <LoadingSpinner />
            ) : largeAttachments.length === 0 ? (
              <div className="text-center text-gray-400 py-8 text-sm">Keine Anhänge vorhanden.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 uppercase text-xs">
                    <tr>
                      {['Dateiname', 'Bezug', 'Datum', 'Größe', ''].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {largeAttachments.map(att => (
                      <tr key={`${att.source}-${att.id}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 max-w-[220px] truncate" title={att.filename}>{att.filename}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[240px]">
                          <div className="truncate" title={att.mail_subject}>{att.mail_subject || '(kein Betreff)'}</div>
                          <div className="truncate text-gray-400">
                            {att.source === 'sent' ? 'An: ' : 'Von: '}{att.mail_counterpart}
                          </div>
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {att.mail_date ? new Date(att.mail_date).toLocaleDateString('de-DE') : '–'}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-700 whitespace-nowrap">{formatSize(att.size_bytes)}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex flex-col items-start gap-1">
                            <button
                              onClick={() => handleDownloadAttachment(att)}
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                              ↓ Herunterladen
                            </button>
                            <button
                              onClick={() => handleDeleteAttachment(att)}
                              disabled={attachmentActionId === att.id}
                              className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50 whitespace-nowrap">
                              {attachmentActionId === att.id ? '…' : '🗑 Löschen'}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tab 4: Spam-Filter ─────────────────────────────────────────────── */}
      {tab === 4 && customerSettings && (
        <div className="max-w-lg">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-gray-800 mb-1">KI-Spam-Filterung</h2>
              <p className="text-sm text-gray-500">
                Eingehende E-Mails werden von der KI auf Spam analysiert.
                Lege hier fest, was mit erkannten Spam-Mails passiert.
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Spam-Aktion
              </label>
              <div className="space-y-2">
                {[
                  { value: 'off', label: 'Deaktiviert', desc: 'Keine Spam-Erkennung.' },
                  { value: 'folder', label: 'In Spam-Ordner verschieben', desc: 'Spam-Mails werden markiert und können im Postfach gefiltert angezeigt werden.' },
                  { value: 'delete', label: 'Automatisch löschen', desc: 'Spam-Mails werden sofort und unwiderruflich gelöscht.' },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    customerSettings.spam_action === opt.value
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="spam_action"
                      value={opt.value}
                      checked={customerSettings.spam_action === opt.value}
                      disabled={!isAdmin}
                      onChange={() => setCustomerSettings(s => ({ ...s, spam_action: opt.value }))}
                      className="mt-0.5 accent-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {!isAdmin && (
                <p className="text-xs text-gray-400 mt-2">
                  Nur Kunden-Admins können diese Einstellung ändern.
                </p>
              )}
            </div>

            {customerSettings.spam_action === 'delete' && (
              <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                <strong>Warnung:</strong> Gelöschte Spam-Mails können nicht wiederhergestellt werden.
                Fehlklassifizierungen durch die KI sind möglich. Prüfe regelmäßig, ob legitime
                Mails fälschlicherweise als Spam erkannt wurden.
              </div>
            )}

            {customerSettings.spam_action === 'folder' && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
                Spam-Mails bleiben erhalten und können über die Schaltfläche <strong>🚫 Spam</strong> im
                Postfach eingesehen und bei Bedarf als „Kein Spam" markiert werden.
              </div>
            )}

            {isAdmin && (
              <button
                onClick={handleSaveSpam}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Tab 5: HTML-Mails ──────────────────────────────────────────────── */}
      {tab === 5 && customerSettings && (
        <div className="max-w-lg">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-gray-800 mb-1">HTML-Mail-Anzeige</h2>
              <p className="text-sm text-gray-500">
                Viele E-Mails enthalten Formatierung (HTML) statt nur reinen Text.
                Lege fest, ob und wie diese Formatierung im Portal angezeigt wird.
                Die Anzeige erfolgt stets in einer isolierten, geschützten Umgebung
                ohne Ausführung von Skripten; externe Bilder werden standardmäßig
                blockiert (Tracking-Schutz).
              </p>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-2">
                Anzeigeverhalten
              </label>
              <div className="space-y-2">
                {[
                  { value: 'text', label: 'Nur Text', desc: 'HTML-Formatierung wird nie angezeigt, auch nicht auf Wunsch. Am sichersten.' },
                  { value: 'ask', label: 'Nachfragen (empfohlen)', desc: 'Mails werden standardmäßig als Text angezeigt, mit einem Button zum Umschalten auf die formatierte Ansicht.' },
                  { value: 'html', label: 'Immer HTML anzeigen', desc: 'Formatierte Mails werden direkt formatiert angezeigt, sofern vorhanden.' },
                ].map(opt => (
                  <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    customerSettings.html_mail_display === opt.value
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300'
                  } ${!isAdmin ? 'opacity-50 cursor-not-allowed' : ''}`}>
                    <input
                      type="radio"
                      name="html_mail_display"
                      value={opt.value}
                      checked={customerSettings.html_mail_display === opt.value}
                      disabled={!isAdmin}
                      onChange={() => setCustomerSettings(s => ({ ...s, html_mail_display: opt.value }))}
                      className="mt-0.5 accent-blue-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-800">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
              {!isAdmin && (
                <p className="text-xs text-gray-400 mt-2">
                  Nur Kunden-Admins können diese Einstellung ändern.
                </p>
              )}
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 text-sm text-blue-700">
              Links in Mails öffnen immer sicher in einem neuen Tab. Eingebettete Skripte
              werden serverseitig entfernt und können in der Anzeige zusätzlich nicht
              ausgeführt werden.
            </div>

            {isAdmin && (
              <button
                onClick={handleSaveHtmlMailDisplay}
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Speichern...' : 'Speichern'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Tab 6: Benachrichtigungen ──────────────────────────────────────── */}
      {tab === 6 && meSettings && (
        <div className="max-w-lg space-y-6">
          {/* Push-Benachrichtigungen (Browser / installierte App) */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-gray-800 mb-1">Push-Benachrichtigungen</h2>
              <p className="text-sm text-gray-500">
                Direkte Benachrichtigung auf diesem Gerät (Desktop-Browser oder installierte
                App auf dem Handy), sobald neue E-Mails eintreffen. Muss pro Gerät einzeln
                aktiviert werden.
              </p>
            </div>

            {!pushSupported && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-600">
                Dieser Browser unterstützt keine Push-Benachrichtigungen.
              </div>
            )}

            {pushSupported && pushPermission === 'denied' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700">
                Benachrichtigungen wurden für diese Seite blockiert. Bitte in den
                Browser-/System-Einstellungen für diese Seite wieder erlauben, um sie hier
                zu aktivieren.
              </div>
            )}

            {pushSupported && pushPermission !== 'denied' && (
              <div className="flex items-center justify-between py-3 border-b border-gray-100">
                <div>
                  <div className="text-sm font-medium text-gray-800">
                    Auf diesem Gerät {pushSubscribed ? 'aktiviert' : 'deaktiviert'}
                  </div>
                  <div className="text-xs text-gray-500">
                    Betrifft nur dieses Gerät/diesen Browser — andere Geräte separat aktivieren.
                  </div>
                </div>
                <button
                  onClick={pushSubscribed ? handleDisablePush : handleEnablePush}
                  disabled={pushBusy}
                  className={`px-4 py-2 text-sm rounded-lg disabled:opacity-50 whitespace-nowrap ${
                    pushSubscribed
                      ? 'border border-gray-300 text-gray-600 hover:bg-gray-50'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}>
                  {pushBusy ? 'Bitte warten…' : pushSubscribed ? 'Deaktivieren' : 'Aktivieren'}
                </button>
              </div>
            )}

            {pushError && (
              <div className="text-xs text-red-600">{pushError}</div>
            )}
          </div>

          {/* E-Mail-Benachrichtigung */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-5">
            <div>
              <h2 className="font-semibold text-gray-800 mb-1">E-Mail-Benachrichtigung</h2>
              <p className="text-sm text-gray-500">
                Konfiguriere, wann du zusätzlich per E-Mail informiert werden möchtest.
              </p>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-gray-100">
              <div>
                <div className="text-sm font-medium text-gray-800">Neue E-Mail eingegangen</div>
                <div className="text-xs text-gray-500">
                  Benachrichtigung an {meSettings.email} bei jeder neu empfangenen Mail
                </div>
              </div>
              <button
                onClick={() => handleToggleNotify(!meSettings.notify_on_new_mail)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                  meSettings.notify_on_new_mail ? 'bg-blue-600' : 'bg-gray-200'
                }`}>
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  meSettings.notify_on_new_mail ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>

            <p className="text-xs text-gray-400">
              Hinweis: Der Versand von Benachrichtigungen setzt einen konfigurierten SMTP-Server voraus.
              Wende dich bei Fragen an deinen Administrator.
            </p>
          </div>
        </div>
      )}

      {/* ── Tab 7: Kontaktliste ────────────────────────────────────────────── */}
      {tab === 7 && (
        <div className="max-w-3xl">
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="font-semibold text-gray-800 mb-1">Kontaktliste</h2>
            <p className="text-sm text-gray-500 mb-4">
              Adressen, an die bereits eine E-Mail gesendet wurde, werden hier automatisch
              gesammelt und beim Verfassen einer neuen Mail im An-Feld vorgeschlagen. Gilt für
              den gesamten Kunden-Account (alle Benutzer teilen sich diese Liste).
            </p>
            {contactsLoading ? (
              <LoadingSpinner />
            ) : contacts.length === 0 ? (
              <div className="text-center text-gray-400 py-8 text-sm">
                Noch keine Kontakte. Sobald eine Mail erfolgreich versendet wurde, erscheint die
                Adresse hier.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-gray-500 uppercase text-xs">
                    <tr>
                      {['Anzeigename', 'E-Mail-Adresse', 'Verwendungen', 'Zuletzt verwendet', ''].map(h => (
                        <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {contacts.map(c => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2.5 max-w-[220px]">
                          {editingContactId === c.id ? (
                            <input
                              autoFocus
                              value={editContactName}
                              onChange={e => setEditContactName(e.target.value)}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveContactName(c); if (e.key === 'Escape') cancelEditContact(); }}
                              className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                            />
                          ) : (
                            <span className="truncate block">{c.display_name || <span className="text-gray-400">–</span>}</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 font-mono">{c.email}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500">{c.use_count}</td>
                        <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {c.last_used_at ? new Date(c.last_used_at).toLocaleDateString('de-DE') : '–'}
                        </td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center gap-3">
                            {editingContactId === c.id ? (
                              <>
                                <button
                                  onClick={() => handleSaveContactName(c)}
                                  disabled={contactActionId === c.id}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium disabled:opacity-50 whitespace-nowrap">
                                  Speichern
                                </button>
                                <button
                                  onClick={cancelEditContact}
                                  className="text-xs text-gray-500 hover:text-gray-700 whitespace-nowrap">
                                  Abbrechen
                                </button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => startEditContact(c)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-medium whitespace-nowrap">
                                  ✎ Bearbeiten
                                </button>
                                <button
                                  onClick={() => handleDeleteContact(c)}
                                  disabled={contactActionId === c.id}
                                  className="text-xs text-red-600 hover:text-red-800 font-medium disabled:opacity-50 whitespace-nowrap">
                                  {contactActionId === c.id ? '…' : '🗑 Löschen'}
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
